import {
  ModalDialog,
  Mtds,
  Contracts,
  Labs,
  SinglePlan,
  UpdatePlan,
  useGetContractsQuery,
  useGetLabsQuery,
  SaveError,
  useUpdatePlanMutation,
  buildUpdatePlanBody,
  hasDirectPlanValues,
  UserMessageError,
  templates,
  widgets,
  useGetOkProgramsQuery,
  useGetPurposesQuery,
  useGetPlanByIdQuery,
  useAuth,
  useGetWorkspacesQuery
} from 'prove-common'
import {
  Form,
  IChangeEvent,
  RJSFSchema,
  UiSchema,
  customizeValidator
} from 'prove-common/rjsf'
import { useEffect, useState } from 'react'
import styles from './EditPlanCard.module.css'
import {
  planInheritanceControls,
  activePlanFormData,
  retainInheritedPlanDrafts,
  withPlanInheritanceControls,
  type PlanField
} from '../plan-field-inheritance/PlanFieldInheritance'
import { validPlanContracts } from '../plan-field-inheritance/planFormOptions'
import { planWorkspaceOptions } from '../plan-field-inheritance/workspaceLabels'

const OK_PROGRAM_PURPOSE_NAME = 'OK-program'

function isOkProgramPurpose(
  purposeId: number | undefined,
  purposes: { id: number; name: string }[] | undefined
): boolean {
  if (purposeId == null || purposes == null) {
    return false
  }

  return purposes.some(
    (purpose) =>
      purpose.id === purposeId && purpose.name === OK_PROGRAM_PURPOSE_NAME
  )
}

const planSchema = (
  labs: Labs,
  contracts: Contracts,
  okPrograms: { id: number; number: string; name: string }[],
  purposes: { id: number; name: string }[] | undefined,
  inherited: Record<Exclude<PlanField, 'workspaceId'>, boolean>,
  requiresOkProgram: boolean
): RJSFSchema => {
  return {
    type: 'object',
    properties: {
      description: {
        type: 'string',
        title: 'Tittel',
        description: 'Gi planen en kort og beskrivende tittel'
      },
      due: {
        type: 'string',
        format: 'date',
        title: 'Frist',
        description: 'Når skal planen være ferdig?',
        ...(inherited.due ? { readOnly: true } : {})
      },
      labId: {
        type: 'number',
        title: 'Laboratorium',
        description: 'Velg hvilket laboratorium planen gjelder for',
        oneOf: labs?.map((lab) => ({
          const: lab.id,
          title: lab.name
        }))
      },
      contractId: {
        type: 'number',
        title: 'Kontrakt',
        description: 'Velg hvilken kontrakt planen gjelder for',
        oneOf: contracts?.flatMap((contract) => ({
          const: contract.id,
          title: contract.title
        }))
      },
      contractRef: {
        type: 'string',
        title: 'Referanse til lab',
        description: 'Referanse til hvilket OK-program det hører til',
        maxLength: 50,
        ...(inherited.contractRef ? { readOnly: true } : {})
      },
      purposeId: {
        type: 'number',
        title: 'Hensikt',
        description: 'Velg hensikt for planen',
        oneOf: (purposes ?? []).map((purpose) => ({
          const: purpose.id,
          title: purpose.name
        })),
        ...(inherited.purposeId ? { readOnly: true } : {})
      },
      okProgramId: {
        type: 'number',
        title: 'Velg OK-program',
        description: 'Velg OK-nummer og navn',
        oneOf: okPrograms.map((r) => ({
          const: r.id,
          title: `${r.number} ${r.name}`
        }))
      }
    },
    required: [
      ...(!inherited.due ? ['due'] : []),
      'description',
      'contractId',
      ...(requiresOkProgram ? ['okProgramId'] : [])
    ]
  }
}

const planUiSchema = (
  showContract: boolean,
  showOkProgramId: boolean
): UiSchema => ({
  'ui:classNames': 'max-w-[832px]',
  'ui:options': {
    allowWrap: true
  },
  due: {
    'ui:widget': 'selectDueDateWidget'
  },
  labId: {
    'ui:widget': showContract ? undefined : 'hidden',
    'ui:classNames': `${showContract ? '' : 'hidden'} basis-full max-w-initial`
  },
  contractId: {
    'ui:classNames': `${showContract ? '' : 'hidden'} basis-full max-w-initial`
  },
  purposeId: {
    'ui:classNames': 'basis-full max-w-initial'
  },
  okProgramId: {
    'ui:widget': showOkProgramId ? undefined : 'hidden',
    'ui:classNames': `${showOkProgramId ? '' : 'hidden'} basis-full max-w-initial`
  }
})

type EditPlanCardProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  plan: SinglePlan
}

type FormData = {
  description?: string
  due?: string
  contractRef?: string
  contractId?: number
  labId?: number
  purposeId?: number
  okProgramId?: number
}

type PlanDraft = {
  planId: number
  isOpen: boolean
  formData: FormData | undefined
  inherit: Record<Exclude<PlanField, 'workspaceId'>, boolean>
}

type EditPlanSessionProps = EditPlanCardProps

function defaultPlanInheritance(
  plan: SinglePlan
): Record<Exclude<PlanField, 'workspaceId'>, boolean> {
  const hasParent = plan.parents?.[0]?.id != null
  return {
    due: hasParent && plan.directDue == null,
    contractRef: hasParent && plan.directContractRef == null,
    purposeId: hasParent && plan.directPurposeId == null
  }
}

type ParentPlanQuery = ReturnType<typeof useGetPlanByIdQuery>

function isParentUnavailable(query: ParentPlanQuery) {
  return query.isPending || query.isError || query.data == null
}

function ParentPlanStatus({
  hasParent,
  query
}: {
  hasParent: boolean
  query: ParentPlanQuery
}) {
  if (!hasParent) return null
  if (query.isError)
    return (
      <Mtds.Flex data-align='center'>
        <Mtds.Alert data-color='danger'>
          Kunne ikke hente overordnet plan.
        </Mtds.Alert>
        <Mtds.Button
          type='button'
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Prøv igjen
        </Mtds.Button>
      </Mtds.Flex>
    )
  if (query.isPending) return null
  if (!query.data)
    return (
      <Mtds.Alert data-color='danger'>
        Overordnet plan er ikke tilgjengelig.
      </Mtds.Alert>
    )
  return null
}

function ParentPlanLoadingStatus({ pending }: { pending: boolean }) {
  const [showStatus, setShowStatus] = useState(false)
  useEffect(() => {
    if (!pending) return
    const timeout = window.setTimeout(() => setShowStatus(true), 1000)
    return () => {
      window.clearTimeout(timeout)
      setShowStatus(false)
    }
  }, [pending])
  const visible = pending && showStatus
  return (
    <Mtds.Muted role='status'>
      <span
        className={styles.loadingStatus}
        data-visible={visible}
        aria-hidden={!visible}
      >
        Henter overordnet plan…
      </span>
    </Mtds.Muted>
  )
}

function normalizeFormData(
  formData: FormData | undefined
): FormData | undefined {
  if (!formData) return formData

  const normalized = { ...formData }

  if (typeof normalized.labId !== 'number') {
    delete normalized.labId
  }

  if (typeof normalized.okProgramId !== 'number') {
    delete normalized.okProgramId
  }

  if (typeof normalized.purposeId !== 'number') {
    delete normalized.purposeId
  }

  return normalized
}

function toFormData(plan: SinglePlan, labId: number | undefined): FormData {
  return (
    normalizeFormData({
      description: plan.description,
      due: plan.directDue ?? undefined,
      contractRef: plan.directContractRef ?? undefined,
      contractId: plan.contractId,
      labId,
      purposeId: plan.directPurposeId ?? undefined,
      okProgramId: plan.okProgramId
    }) ?? {}
  )
}

function draftWithLab(
  draft: FormData | undefined,
  labId: number | undefined,
  hasChangedLab: boolean
): FormData {
  return {
    ...draft,
    labId: hasChangedLab ? draft?.labId : (draft?.labId ?? labId)
  }
}

function parentPurposeLabel(
  parent: SinglePlan | undefined,
  purposes: { id: number; name: string }[] | undefined
) {
  if (parent?.purposeId == null) return undefined
  return (
    purposes?.find((item) => item.id === parent.purposeId)?.name ??
    `Hensikt ${parent.purposeId}`
  )
}

function hasNoDescendantWithTasks(plan: SinglePlan): boolean {
  if ((plan.tasks ?? []).length > 0) return false
  for (const child of plan.children ?? []) {
    if (!hasNoDescendantWithTasks(child)) return false
  }
  return true
}

function updatePlanValues(
  formData: FormData,
  inherit: Record<Exclude<PlanField, 'workspaceId'>, boolean>,
  usesOkProgram: boolean
) {
  return {
    due: inherit.due ? null : formData.due?.trim() || null,
    description: formData.description,
    contractRef: inherit.contractRef
      ? null
      : formData.contractRef?.trim()
        ? formData.contractRef
        : null,
    contractId: formData.contractId,
    purposeId: inherit.purposeId ? null : (formData.purposeId ?? null),
    okProgramId: usesOkProgram ? (formData.okProgramId ?? null) : null
  }
}

function EditPlanSession({
  isOpen,
  setOpen,
  plan
}: Readonly<EditPlanSessionProps>) {
  const [baselinePlan] = useState(plan)
  const updatePlan = useUpdatePlanMutation()
  const contracts = useGetContractsQuery(isOpen)
  const parentId = plan.parents?.[0]?.id
  const { isAdmin, permissions = [] } = useAuth()
  const canEditWorkspaces =
    parentId == null &&
    (isAdmin ||
      permissions.some(
        (permission) => permission.responsibility === 'PROGRAM_MANAGER'
      ))
  const workspaceQuery = useGetWorkspacesQuery(isOpen && canEditWorkspaces)
  const [workspaceId, setWorkspaceId] = useState(baselinePlan.directWorkspaceId)
  const [fagseksjonWorkspaceId, setFagseksjonWorkspaceId] = useState(
    baselinePlan.fagseksjonWorkspaceId ?? null
  )
  const workspaceChanged =
    workspaceId !== baselinePlan.directWorkspaceId ||
    fagseksjonWorkspaceId !== (baselinePlan.fagseksjonWorkspaceId ?? null)
  const validWorkspaces =
    !canEditWorkspaces ||
    !workspaceChanged ||
    (workspaceQuery.data?.some(
      (workspace) =>
        workspace.id === workspaceId &&
        workspace.active &&
        workspace.kind?.code === 'TILSYNSDIVISJON'
    ) &&
      workspaceQuery.data?.some(
        (workspace) =>
          workspace.id === fagseksjonWorkspaceId &&
          workspace.active &&
          workspace.kind?.code === 'FAGSEKSJON'
      ))
  const parentQuery = useGetPlanByIdQuery(
    isOpen && parentId != null ? parentId : undefined
  )
  const parentPlan = parentQuery.data
  const { data: okPrograms } = useGetOkProgramsQuery(isOpen)
  const { data: purposes } = useGetPurposesQuery(isOpen)
  const contractId = plan?.contractId
  const contract = contracts?.data?.find(
    (contract) => contract.id === contractId
  )
  const labId = contract?.labId

  const defaultInheritance = defaultPlanInheritance(plan)
  const parentUnavailable = isParentUnavailable(parentQuery)
  const [draftState, setDraftState] = useState<{
    planId: number
    isOpen: boolean
    formData: FormData | undefined
    inherit: Record<Exclude<PlanField, 'workspaceId'>, boolean>
  }>(() => ({
    planId: plan.id,
    isOpen,
    formData: toFormData(plan, labId),
    inherit: defaultInheritance
  }))
  const hasNewDraftContext =
    draftState.planId !== plan.id || draftState.isOpen !== isOpen
  const draftFormData = hasNewDraftContext
    ? toFormData(plan, labId)
    : draftState.formData
  const [hasChangedLab, setHasChangedLab] = useState(false)
  const formData = draftWithLab(draftFormData, labId, hasChangedLab)
  const inherit = hasNewDraftContext ? defaultInheritance : draftState.inherit
  const [saveError, setSaveError] = useState<unknown>()
  const updateDraft = (
    update: PlanDraft | ((current: PlanDraft) => PlanDraft)
  ) => {
    setDraftState((current) => {
      const base: PlanDraft = hasNewDraftContext
        ? {
            planId: plan.id,
            isOpen,
            formData: toFormData(plan, labId),
            inherit: defaultInheritance
          }
        : current
      const next = typeof update === 'function' ? update(base) : update
      return { ...next, planId: plan.id, isOpen }
    })
  }

  const validContracts = validPlanContracts(
    contracts?.data,
    formData?.labId,
    inherit.due ? parentPlan?.due : formData.due
  )

  const editLab = hasNoDescendantWithTasks(plan)
  const { data: labs } = useGetLabsQuery(isOpen)
  const effectivePurposeId = inherit.purposeId
    ? parentPlan?.purposeId
    : formData?.purposeId
  const showOkProgramId = isOkProgramPurpose(effectivePurposeId, purposes)
  const onSubmit = async ({
    formData
  }: IChangeEvent<FormData>): Promise<void> => {
    if (formData == null || updatePlan.isPending) return
    setSaveError(undefined)
    try {
      if (baselinePlan.etag == null) throw new Error('Planen mangler ETag.')
      if (!validWorkspaces)
        throw new UserMessageError('Velg fagseksjon og tilsynsdivisjon.')
      if (!hasDirectPlanValues(baselinePlan)) {
        throw new UserMessageError(
          'Kunne ikke oppdatere planen fordi noen planopplysninger mangler. Hent planen på nytt og prøv igjen.'
        )
      }
      const usesOkProgram = isOkProgramPurpose(
        inherit.purposeId ? parentPlan?.purposeId : formData.purposeId,
        purposes
      )
      const updatedPlan: UpdatePlan = buildUpdatePlanBody(baselinePlan, {
        ...updatePlanValues(formData, inherit, usesOkProgram),
        ...(canEditWorkspaces ? { workspaceId, fagseksjonWorkspaceId } : {})
      })
      await updatePlan.mutateAsync({
        planId: plan.id,
        etag: baselinePlan.etag,
        body: updatedPlan
      })
      setOpen(false)
    } catch (error) {
      setSaveError(error)
    }
  }
  const validator = customizeValidator()
  const parentValues = {
    due: parentPlan?.due,
    contractRef: parentPlan?.contractRef,
    purposeId: parentPurposeLabel(parentPlan, purposes)
  }
  const changeInheritance = (
    field: Exclude<PlanField, 'workspaceId'>,
    checked: boolean
  ) => {
    if (parentPlan == null || parentUnavailable) return
    const inheritedValues = {
      due: parentPlan.due,
      contractRef: parentPlan.contractRef,
      purposeId: parentPlan.purposeId
    }
    updateDraft((current) => ({
      ...current,
      formData: !checked
        ? {
            ...current.formData,
            [field]: current.formData?.[field] ?? inheritedValues[field]
          }
        : current.formData,
      inherit: { ...current.inherit, [field]: checked }
    }))
  }
  const inheritanceControls = planInheritanceControls({
    hasParent: parentId != null,
    inherited: inherit,
    disabled: {
      due: parentUnavailable,
      contractRef: parentUnavailable,
      purposeId: parentUnavailable
    },
    onChange: changeInheritance
  })
  return (
    <ModalDialog
      id={'edit-plan-modal'}
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor={'edit-plan-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Rediger plan</Mtds.Heading>
        {canEditWorkspaces && (
          <Mtds.Grid data-gap='3'>
            <Mtds.Field
              as='select'
              label='Fagseksjon'
              value={fagseksjonWorkspaceId ?? ''}
              disabled={
                workspaceQuery.isPending ||
                workspaceQuery.isError ||
                updatePlan.isPending
              }
              onChange={(event) =>
                setFagseksjonWorkspaceId(Number(event.target.value) || null)
              }
              options={[
                { value: '', label: 'Velg fagseksjon' },
                ...planWorkspaceOptions(
                  workspaceQuery.data?.filter(
                    (workspace) => workspace.kind?.code === 'FAGSEKSJON'
                  ),
                  [fagseksjonWorkspaceId]
                ).map((workspace) => ({
                  value: String(workspace.id),
                  label: workspace.name
                }))
              ]}
            />
            <Mtds.Field
              as='select'
              label='Tilsynsdivisjon'
              value={workspaceId ?? ''}
              disabled={
                workspaceQuery.isPending ||
                workspaceQuery.isError ||
                updatePlan.isPending
              }
              onChange={(event) =>
                setWorkspaceId(Number(event.target.value) || null)
              }
              options={[
                { value: '', label: 'Velg tilsynsdivisjon' },
                ...planWorkspaceOptions(
                  workspaceQuery.data?.filter(
                    (workspace) => workspace.kind?.code === 'TILSYNSDIVISJON'
                  ),
                  [workspaceId]
                ).map((workspace) => ({
                  value: String(workspace.id),
                  label: workspace.name
                }))
              ]}
            />
            {workspaceQuery.isError && (
              <Mtds.Alert data-color='danger'>
                Kunne ikke hente arbeidsområder.
              </Mtds.Alert>
            )}
            {!validWorkspaces && (
              <Mtds.Alert data-color='warning'>
                Velg fagseksjon og tilsynsdivisjon.
              </Mtds.Alert>
            )}
          </Mtds.Grid>
        )}
        {formData?.labId && validContracts?.length === 0 && (
          <Mtds.Alert data-color='warning'>
            Det finnes ingen gyldig kontrakt for valgt laboratorium og frist.
            Opprett en ny kontrakt, eller velg en annen kombinasjon.
          </Mtds.Alert>
        )}
        <SaveError error={saveError} action='oppdatere planen' />
        <ParentPlanStatus hasParent={parentId != null} query={parentQuery} />
        <Form
          noHtml5Validate
          schema={planSchema(
            labs ?? [],
            validContracts ?? [],
            okPrograms ?? [],
            purposes,
            inherit,
            showOkProgramId
          )}
          uiSchema={withPlanInheritanceControls(
            planUiSchema(editLab, showOkProgramId),
            inheritanceControls,
            inherit,
            parentValues
          )}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={activePlanFormData(formData, inherit)}
          onSubmit={(data) => onSubmit(data)}
          onChange={(data, id) => {
            if (id === 'root_labId') setHasChangedLab(true)
            const normalized = normalizeFormData(data.formData)

            if (
              normalized != null &&
              !isOkProgramPurpose(
                inherit.purposeId
                  ? parentPlan?.purposeId
                  : normalized.purposeId,
                purposes
              )
            ) {
              delete normalized.okProgramId
            }

            if (normalized != null) {
              updateDraft((current) => ({
                ...current,
                formData: retainInheritedPlanDrafts(
                  normalized,
                  current.formData,
                  inherit
                )
              }))
            }
          }}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
          transformErrors={(errors) =>
            errors.map((error) => {
              if (error.property === '.labId') {
                error.message = 'Du må velge laboratorium for planen'
              }
              return error
            })
          }
        >
          <Mtds.Flex className='mt-8' data-align='center'>
            <Mtds.Button command='close' commandfor={'edit-plan-modal'}>
              Forkast
            </Mtds.Button>
            <Mtds.Button
              type='submit'
              aria-busy={updatePlan.isPending}
              disabled={
                updatePlan.isPending ||
                !validWorkspaces ||
                (parentId != null && parentUnavailable)
              }
              data-variant='primary'
            >
              Lagre
            </Mtds.Button>
            {parentId != null && (
              <ParentPlanLoadingStatus pending={parentQuery.isPending} />
            )}
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}

export default function EditPlanCard(props: Readonly<EditPlanCardProps>) {
  const sessionKey = `${props.plan.id}:${props.isOpen ? 'open' : 'closed'}`
  return <EditPlanSession key={sessionKey} {...props} />
}
